/**
 * PageHeader:
 *
 * A component that renders a page's title and subtitle, and an optional action buttons.
 * buttons are rendered in the top right corner of the header.
 * Buttons should contain actions specific to the page, such as "Add" or "Edit".
 *
 * Any site navigation links should be rendered in the NavigationBar instead.
 */
import { Box, Flex, Heading } from '@chakra-ui/react';

interface PageHeaderProps {
  title?: string | React.ReactNode;
  subtitle?: string | React.ReactNode;
  children?: React.ReactNode;
  bgColor?: string;
  textColor?: string;
}

export function PageHeader({
  title,
  subtitle,
  children,
  bgColor,
  textColor,
}: PageHeaderProps) {
  return (
    <Flex
      width="100%"
      alignItems="center"
      px={8}
      py={5}
      bg={bgColor ?? 'mw.panelGrey'}
      justifyContent="space-between"
      flexShrink={0}
      minHeight={20}
    >
      <Box>
        <Heading
          fontWeight={100}
          fontSize={30}
          color={textColor ?? 'mw.darkGrey'}
        >
          {title}
        </Heading>

        {subtitle && (
          <Heading
            fontWeight="400"
            fontSize={16}
            mt={2}
            color={textColor ?? 'mw.darkGrey'}
          >
            {subtitle}
          </Heading>
        )}
      </Box>

      <Box marginLeft="auto" alignItems="center">
        <Flex
          alignContent="end"
          direction={{ base: 'column-reverse', md: 'row' }}
          gap={2}
          justifyContent="end"
        >
          {children}
        </Flex>
      </Box>
    </Flex>
  );
}
